import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Plus, Calendar, List, Download } from 'lucide-react';
import type FullCalendar from '@fullcalendar/react';
import CalendarNavigation from '../../manufacture/calendar/CalendarNavigation';
import MarketingMonthCalendar from '../calendar/MarketingMonthCalendar';
import MarketingActionGrid from '../list/MarketingActionGrid';
import type { MarketingActionDto } from '../list/MarketingActionGrid';
import MarketingActionFilters, {
  EMPTY_FILTERS,
  type MarketingFilters,
} from '../list/MarketingActionFilters';
import MarketingActionModal from '../detail/MarketingActionModal';
import ImportFromOutlookModal from '../detail/ImportFromOutlookModal';
import {
  useMarketingCalendar,
  useMarketingActions,
  useMarketingAction,
  useMoveMarketingAction,
} from '../../../api/hooks/useMarketingCalendar';
import type {
  MarketingActionDto as ApiMarketingActionDto,
  MarketingActionCalendarDto as ApiMarketingActionCalendarDto,
  MarketingActionType,
} from '../../../api/generated/api-client';
import { formatDateStr } from '../calendar/fullcalendarAdapters';
import type { CalendarEvent } from '../calendar/fullcalendarAdapters';
import { PAGE_CONTAINER_HEIGHT } from '../../../constants/layout';
import { usePermissionsContext } from '../../../auth/PermissionsContext';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import { MobileAgendaView } from '../calendar/MobileAgendaView';
import { useScreenView } from '../../../telemetry/useScreenView';

const CZECH_MONTHS = [
  'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
  'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec',
];

function startOfWeekMonday(date: Date): Date {
  const clone = new Date(date);
  const dow = clone.getDay(); // 0 = Sunday
  const daysToMonday = dow === 0 ? 6 : dow - 1;
  clone.setHours(0, 0, 0, 0);
  clone.setDate(clone.getDate() - daysToMonday);
  clone.setHours(0, 0, 0, 0);
  return clone;
}

function getCalendarStartForToday(viewMode: 'fiveWeeks' | 'twoWeeks'): Date {
  const monday = startOfWeekMonday(new Date());
  if (viewMode === 'fiveWeeks') {
    monday.setDate(monday.getDate() - 7);
  }
  return monday;
}

function mapFolderLinks(
  links?: Array<{ folderKey?: string; folderType?: string }>,
): Array<{ path: string; folderType?: string }> {
  return (links ?? []).map((fl) => ({ path: fl.folderKey ?? '', folderType: fl.folderType }));
}

type ViewMode = 'fiveWeeks' | 'twoWeeks' | 'list';

const MarketingCalendarPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('fiveWeeks');
  const [currentDate, setCurrentDate] = useState(() => getCalendarStartForToday('fiveWeeks'));
  const [visibleRange, setVisibleRange] = useState<{ start: Date; end: Date } | null>(null);
  const [filters, setFilters] = useState<MarketingFilters>(EMPTY_FILTERS);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedActionId, setSelectedActionId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAction, setEditingAction] = useState<MarketingActionDto | null>(null);
  const [prefillDates, setPrefillDates] = useState<{ dateFrom: string; dateTo: string } | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const viewModeSubScreen =
    viewMode === 'fiveWeeks' ? 'FiveWeeksView' :
    viewMode === 'twoWeeks' ? 'TwoWeeksView' :
    'ListView';
  useScreenView('Marketing', 'MarketingCalendar', viewModeSubScreen);
  const isMobile = useIsMobile();

  const calendarRef = useRef<FullCalendar>(null);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode !== 'list') {
      setVisibleRange(null);
      setCurrentDate(getCalendarStartForToday(mode));
    }
  };

  const { hasPermission } = usePermissionsContext();
  const isAdmin = hasPermission('marketing.marketing_calendar.write');

  const { startDate, endDate } = useMemo(() => {
    if (visibleRange) {
      return { startDate: visibleRange.start, endDate: visibleRange.end };
    }
    const mode = viewMode === 'list' ? 'fiveWeeks' : viewMode;
    const start = getCalendarStartForToday(mode);
    const end = new Date(start);
    end.setDate(start.getDate() + (mode === 'twoWeeks' ? 14 : 35));
    return { startDate: start, endDate: end };
  }, [visibleRange, viewMode]);

  const calendarQuery = useMarketingCalendar({ startDate, endDate });
  const listQuery = useMarketingActions({
    pageNumber,
    searchTerm: filters.searchText || undefined,
    actionType: filters.actionType || undefined,
    startDateFrom: filters.dateFrom ? new Date(filters.dateFrom) : undefined,
    startDateTo: filters.dateTo ? new Date(filters.dateTo) : undefined,
  });
  const detailQuery = useMarketingAction(selectedActionId ?? 0);
  const moveMutation = useMoveMarketingAction();

  const calendarEvents: CalendarEvent[] = useMemo(
    () =>
      (calendarQuery.data?.actions ?? []).map((a: ApiMarketingActionCalendarDto) => ({
        id: a.id!,
        title: a.title ?? '',
        actionType: (a.actionType ?? 'Other') as MarketingActionType,
        dateFrom: a.startDate ? formatDateStr(a.startDate) : '',
        dateTo: a.endDate ? formatDateStr(a.endDate) : '',
        associatedProducts: a.associatedProducts ?? [],
        outlookSyncStatus: a.outlookSyncStatus,
      })),
    [calendarQuery.data],
  );

  const listActions: MarketingActionDto[] = useMemo(
    () =>
      (listQuery.data?.actions ?? []).map((a: ApiMarketingActionDto) => ({
        id: a.id,
        title: a.title,
        detail: a.description,
        actionType: a.actionType,
        dateFrom: a.startDate,
        dateTo: a.endDate,
        associatedProducts: a.associatedProducts,
        folderLinks: mapFolderLinks(a.folderLinks),
        outlookSyncStatus: a.outlookSyncStatus,
      })),
    [listQuery.data],
  );

  const totalPages: number = listQuery.data?.totalPages ?? 1;

  const periodLabel = useMemo(() => {
    const start = visibleRange?.start ?? currentDate;
    const rawEnd = visibleRange?.end ?? currentDate;
    const end = new Date(rawEnd);
    end.setDate(end.getDate() - 1);

    const startMonth = start.getMonth();
    const startYear = start.getFullYear();
    const endMonth = end.getMonth();
    const endYear = end.getFullYear();

    if (startYear === endYear && startMonth === endMonth) {
      return `${CZECH_MONTHS[startMonth]} ${startYear}`;
    }
    if (startYear === endYear) {
      return `${CZECH_MONTHS[startMonth]} – ${CZECH_MONTHS[endMonth]} ${startYear}`;
    }
    return `${CZECH_MONTHS[startMonth]} ${startYear} – ${CZECH_MONTHS[endMonth]} ${endYear}`;
  }, [visibleRange, currentDate]);

  const goToPrev = () => calendarRef.current?.getApi().prev();
  const goToNext = () => calendarRef.current?.getApi().next();
  const goToToday = () => {
    if (viewMode === 'list') return;
    calendarRef.current?.getApi().gotoDate(getCalendarStartForToday(viewMode));
  };

  const handleDatesSet = useCallback(
    (_visibleStart: Date, _visibleEnd: Date, currentStart: Date) => {
      setCurrentDate(new Date(currentStart));
      setVisibleRange({ start: _visibleStart, end: _visibleEnd });
    },
    [],
  );

  const openCreate = () => {
    setPrefillDates(null);
    setEditingAction(null);
    setIsModalOpen(true);
  };

  const openEdit = (id: number) => {
    setSelectedActionId(id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingAction(null);
    setSelectedActionId(null);
    setPrefillDates(null);
  };

  React.useEffect(() => {
    const a: ApiMarketingActionDto | undefined = detailQuery.data?.action;
    if (a) {
      setEditingAction({
        id: a.id,
        title: a.title,
        detail: a.description,
        actionType: a.actionType,
        dateFrom: a.startDate,
        dateTo: a.endDate,
        associatedProducts: a.associatedProducts,
        folderLinks: mapFolderLinks(a.folderLinks),
      });
    }
  }, [detailQuery.data]);

  const handleEventMove = useCallback(
    (id: number, dateFrom: string, dateTo: string) => {
      moveMutation.mutate({
        id,
        startDate: new Date(dateFrom),
        endDate: new Date(dateTo),
      });
    },
    [moveMutation],
  );

  const handleEventResize = useCallback(
    (id: number, dateFrom: string, dateTo: string) => {
      handleEventMove(id, dateFrom, dateTo);
    },
    [handleEventMove],
  );

  const handleDateRangeSelect = useCallback(
    (dateFrom: string, dateTo: string) => {
      setPrefillDates({ dateFrom, dateTo });
      setEditingAction(null);
      setIsModalOpen(true);
    },
    [],
  );

  if (isMobile) {
    return <MobileAgendaView />;
  }

  return (
    <div className="flex flex-col" style={{ height: PAGE_CONTAINER_HEIGHT }}>
      <div className="flex items-center justify-between px-6 py-4 bg-white dark:bg-graphite-surface border-b border-gray-200 dark:border-graphite-border flex-shrink-0">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-graphite-text">
          Marketingový kalendář
        </h1>
        <div className="flex items-center gap-3">
          <div className="flex border border-gray-200 dark:border-graphite-border rounded-lg overflow-hidden">
            <button
              onClick={() => handleViewModeChange('fiveWeeks')}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${
                viewMode === 'fiveWeeks'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5'
              }`}
            >
              <Calendar className="h-4 w-4" />
              5 týdnů
            </button>
            <button
              onClick={() => handleViewModeChange('twoWeeks')}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${
                viewMode === 'twoWeeks'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5'
              }`}
            >
              <Calendar className="h-4 w-4" />
              14 dní
            </button>
            <button
              onClick={() => handleViewModeChange('list')}
              className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${
                viewMode === 'list'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5'
              }`}
            >
              <List className="h-4 w-4" />
              Seznam
            </button>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nová akce
          </button>
          {isAdmin && (
            <button
              onClick={() => setIsImportModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-graphite-surface-2 text-gray-700 dark:text-graphite-muted border border-gray-300 dark:border-graphite-border rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-white/5 transition-colors"
            >
              <Download className="h-4 w-4" />
              Import z Outlooku
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {viewMode !== 'list' ? (
          <div className="flex flex-col h-full gap-4">
            <div className="flex-shrink-0">
              <CalendarNavigation
                onPrevious={goToPrev}
                onNext={goToNext}
                onToday={goToToday}
                currentPeriodLabel={periodLabel}
              />
            </div>
            {calendarQuery.isLoading ? (
              <div className="text-center py-12 text-gray-500 dark:text-graphite-muted text-sm">
                Načítání...
              </div>
            ) : calendarQuery.error ? (
              <div className="text-center py-12 text-red-500 dark:text-red-400 text-sm">
                Chyba při načítání kalendáře.
              </div>
            ) : (
              <div className="flex-1 min-h-0">
                <MarketingMonthCalendar
                  key={viewMode}
                  viewName={viewMode}
                  events={calendarEvents}
                  initialDate={currentDate}
                  onEventClick={openEdit}
                  onEventMove={handleEventMove}
                  onEventResize={handleEventResize}
                  onDateRangeSelect={handleDateRangeSelect}
                  onDatesSet={handleDatesSet}
                  calendarRef={calendarRef}
                  className="h-full"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <MarketingActionFilters
              filters={filters}
              onChange={(f) => {
                setFilters(f);
                setPageNumber(1);
              }}
              onClear={() => {
                setFilters(EMPTY_FILTERS);
                setPageNumber(1);
              }}
            />
            <div className="bg-white dark:bg-graphite-surface border border-gray-200 dark:border-graphite-border rounded-lg overflow-hidden">
              <MarketingActionGrid
                actions={listActions}
                totalPages={totalPages}
                pageNumber={pageNumber}
                onPageChange={setPageNumber}
                onActionClick={openEdit}
                isLoading={listQuery.isLoading}
              />
            </div>
          </div>
        )}
      </div>

      <MarketingActionModal
        isOpen={isModalOpen}
        onClose={closeModal}
        existingAction={editingAction}
        prefillDates={prefillDates}
      />

      <ImportFromOutlookModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
      />
    </div>
  );
};

export default MarketingCalendarPage;
